import { useMemo } from 'react';
import { useUserStore } from '@Auth/store';
import { CreateContractButtonController } from '@features/create-contract-button';
import { useBrandingOptions } from '@features/create-entity-modal';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useModalDispatch } from '@hooks/useModalProvider';
import { useSubmissions } from '@queries/fetchSubmissions';
import { useTags } from '@queries/fetchTags';
import type { Action, PlainAction } from '@root/@types/types';
import { SUBMISSION_STATUSES } from '@root/@types/types';
import { isNonNullish } from '@root/helpers';
import { isAdmin } from '@root/helpers/permissions/utils';
import withMenuLayout from '@root/src/layout/withMenuLayout';
import { isPPLFrontend } from '@root/src/utils/app-paths';
import { useFeatureFlags, useUserTeams } from '@src/hooks';

import { MODALS } from '../../constants';

import { DownloadCSVButton } from './components/DownloadCSVButton';
import { getSubmissionsTableColumns } from './helpers/columns/getSubmissionsTableColumns';
import { ReactTable } from './components';
import { useDataIsEmpty } from './hooks';

const getInitialFilters = (isPPLFrontend: boolean, hasFirmOrderStatus: boolean) => {
  if (isPPLFrontend) {
    return [];
  }

  if (!hasFirmOrderStatus) {
    return [
      {
        id: 'status',
        value: SUBMISSION_STATUSES.filter((status) => !['ARCHIVED', 'FIRM_ORDER'].includes(status)),
      },
    ];
  }

  return [{ id: 'status', value: SUBMISSION_STATUSES.filter((status) => status !== 'ARCHIVED') }];
};

export const Submissions: React.FC = () => {
  const { showModal } = useModalDispatch();

  const user = useUserStore(({ user }) => user);
  const { data = [], isLoading: isSubmissionsLoading, status } = useSubmissions();
  const { data: tags = [], isLoading: isTagsLoading } = useTags();

  const { data: teams = [], isLoading: isTeamsLoading } = useUserTeams();
  const { brandingFieldName, data: brandingOptions = [] } = useBrandingOptions();

  const isEmpty = useDataIsEmpty(data, status);

  const getFeatureFlagState = useFeatureFlags();

  const hasCommentsFeature = getFeatureFlagState('Comments') ?? false;
  const hasEndorsementsFeature = getFeatureFlagState('Endorsements') ?? false;
  const hasFirmOrderStatus = getFeatureFlagState('FirmOrderStatus') ?? false;
  const disableRecordCreation = getFeatureFlagState('DisableRecordCreation') ?? false;
  const canCreate = isPPLFrontend || !disableRecordCreation;
  const { pbqaEval, pbqaReview } = useFeatureAccess();
  const hasGuidanceFeature = pbqaEval || pbqaReview;

  const initialFilters = useMemo(() => getInitialFilters(isPPLFrontend, hasFirmOrderStatus), [hasFirmOrderStatus]);

  const columns = getSubmissionsTableColumns({
    brandingFieldName,
    brandingOptions,
    disableStatus: isPPLFrontend,
    hasCommentsFeature,
    hasEndorsementsFeature,
    hasFirmOrderStatus,
    hasGuidanceFeature,
    isAdmin: isAdmin(user),
    tags,
    teams,
  });

  const createEntityAction: PlainAction = {
    key: 'create',
    label: 'New Contract',
    onClick: () => showModal(MODALS.ADMIN_CREATE_TEMPLATE, { data }),
  };

  const actions: Action[] = [
    isPPLFrontend && createEntityAction,
    !isPPLFrontend && canCreate && <CreateContractButtonController contracts={data} key="create-contract-button" />,
    isPPLFrontend ? undefined : <DownloadCSVButton key="download-csv" type="submissions" />,
  ].filter(isNonNullish);

  return (
    <ReactTable
      actions={actions}
      columns={columns}
      columnVisibility={{
        branding_id: false,
        branding: false,
        class_of_business: false,
        commentsCount: false,
        created_at: false,
        created_by: false,
        endorsementsCount: false,
        id: false,
        last_updated_by: false,
        missingDatapointsCount: false,
        renewals: false,
        sourceResourceDisplayName: false,
        sourceResourceName: false,
      }}
      columnPinning={{ right: ['view'] }}
      createEntityAction={canCreate ? createEntityAction : undefined}
      entityName="contract"
      filteredData={data}
      initialFilters={initialFilters}
      initialSorting={[{ id: 'created_at', desc: true }]}
      status={status}
      isEmpty={isEmpty}
      isLoading={isSubmissionsLoading || isTagsLoading || isTeamsLoading}
    />
  );
};

export const UserSubmissionsPage = withMenuLayout(Submissions);
